import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ReactComponent as X } from '../../../../assets/icons/close-icon.svg';
import { ReactComponent as Download } from '../../../../assets/icons/download.svg';
import { ReactComponent as MapPin } from '../../../../assets/icons/location-icon.svg';
import { ReactComponent as Zap } from '../../../../assets/icons/zap.svg';
import DefaultApiLimit from '../../../../config/defaultLimit';
import { ApiRequest } from '../../../../interface/common.interface';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import {
  fetchEmspLocationFilters,
  fetchEmspLocations,
  pullEmspLocations,
  setSortData,
  updateEMSPLocationsFilters,
} from '../../../../redux/slices/OCPI/EMSPLocationsSlice';
import { RootState } from '../../../../redux/store';
import MyButton from '../../../../shared-components/button/Button';
import CustomTable from '../../../../shared-components/custom-table/CustomTable';
import ModalBox from '../../../../shared-components/modal-box/ModalBox';
import SuccessNotification from '../../../../shared-components/success-notification/SuccessNotification';
import { GetParametersFromCommonFilters } from '../../../../shared-functions/CommonFilters';
import ConvertUTCtoDateTime from '../../../../shared-functions/ConvertUTCtoDateTime';

// ── Types ─────────────────────────────────────────────────────────────────────

interface OcpiEvse {
  uid: string;
  evse_id?: string;
  status: string;
  connectors?: any[];
  last_updated?: string;
}

interface OcpiLocation {
  id: string;
  name?: string;
  address?: string;
  city?: string;
  country?: string;
  coordinates?: { latitude: string; longitude: string };
  evses?: OcpiEvse[];
  last_updated?: string;
  _objectId?: string;
  _lastUpdated?: string;
  _createdAt?: string;
  createdAt?: string;
  created_at?: string;
  created?: string;
  cpoCountryCode?: string;
  cpoPartyId?: string;
}

// ── Sub-components ─────────────────────────────────────────────────────────────

/**
 * Renders a status badge for an EVSE.
 * @param {any} props The props.
 * @param {any} props.status The status.
 */
function StatusBadge({ status }: Readonly<{ status: string }>) {
  let modifier = 'cpo-status-badge--pending';
  if (status === 'AVAILABLE') {
    modifier = 'cpo-status-badge--available';
  } else if (status === 'CHARGING') {
    modifier = 'cpo-status-badge--charging';
  } else if (status === 'BLOCKED' || status === 'FAULTED') {
    modifier = 'cpo-status-badge--blocked';
  }

  return <span className={`cpo-status-badge ${modifier}`}>{status}</span>;
}

// ── Location Drill-Down Modal ───────────────────────────────────────────────────

/**
 * Renders read-only details of a location received from a connected CPO.
 * @param {any} props The props.
 * @param {any} props.location The location details.
 * @param {any} props.onClose The close callback.
 */
function LocationDetailsModal({
  location,
  onClose,
}: Readonly<{
  location: OcpiLocation;
  onClose: () => void;
}>) {
  const evses: OcpiEvse[] = location.evses || [];

  return (
    <ModalBox
      open
      header={false}
      notification
      title={null}
      closeModal={onClose}
      content={
        <div className="cpo-drilldown-modal">
          {/* Header */}
          <div className="cpo-drilldown-header">
            <div>
              <h3 className="cpo-drilldown-title">{location.name || 'Location Details'}</h3>
              <p className="cpo-drilldown-subtitle">
                <MapPin height={12} width={12} />
                {location.address}
                {location.city ? `, ${location.city}` : ''}
                {location.cpoCountryCode && location.cpoPartyId
                  ? ` · Source: ${location.cpoCountryCode} / ${location.cpoPartyId}`
                  : ''}
              </p>
            </div>
            <button type="button" onClick={onClose} className="cpo-drilldown-close">
              <X width={30} height={30} />
            </button>
          </div>

          {/* EVSE List */}
          <div className="cpo-drilldown-body">
            <div className="cpo-drilldown-select-all-row">
              <span className="cpo-drilldown-select-all-count">
                {evses.length} EVSE{evses.length !== 1 ? 's' : ''} received
              </span>
            </div>

            {evses.length === 0 ? (
              <div className="cpo-drilldown-empty">
                <Zap height={32} width={32} className="cpo-drilldown-empty-icon" />
                <p>No EVSEs found for this location.</p>
              </div>
            ) : (
              <div className="cpo-evse-list">
                {evses.map((evse) => (
                  <div key={evse.uid} className="cpo-evse-row">
                    <div className="cpo-evse-content">
                      <div className="cpo-evse-head">
                        <span className="cpo-evse-id">{evse.evse_id || evse.uid}</span>
                        <StatusBadge status={evse.status || 'UNKNOWN'} />
                      </div>
                      <div className="cpo-evse-sub">
                        {evse.connectors?.length || 0} connector
                        {(evse.connectors?.length || 0) !== 1 ? 's' : ''}
                        {evse.connectors?.[0]?.standard ? ` · ${evse.connectors[0].standard}` : ''}
                        {evse.connectors?.[0]?.power_type
                          ? ` · ${evse.connectors[0].power_type}`
                          : ''}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      }
    />
  );
}

// ── Main Panel ────────────────────────────────────────────────────────────────

interface LocationRow {
  id: string;
  name: string;
  cpo: string;
  address: string;
  evsesCount: number;
  lastUpdated: string;
  _createdAt?: string;
  createdAt: string;
  raw: OcpiLocation;
}

/**
 * EmspLocationsPanel component displays OCPI locations received from connected CPOs.
 * @param {any} props The props.
 * @param {any} props.countryCode The country code.
 * @param {any} props.partyId The party ID.
 * @param {any} props.filterCountryCode Optional country code of the CPO to filter by.
 * @param {any} props.filterPartyId Optional party ID of the CPO to filter by.
 * @param {any} props.refreshTrigger Optional number that triggers a reload when incremented.
 */
export default function EmspLocationsPanel({
  countryCode,
  partyId,
  filterCountryCode,
  filterPartyId,
  refreshTrigger,
}: Readonly<{
  countryCode: string;
  partyId: string;
  filterCountryCode?: string;
  filterPartyId?: string;
  refreshTrigger?: number;
}>) {
  const dispatch = useAppDispatch();
  const { t } = useTranslation(['ocpi']);
  const {
    locations,
    limit,
    page,
    totalPages,
    totalResults,
    loading,
    sortBy,
    sortField,
    filters,
    filterListLoading,
  } = useAppSelector((state: RootState) => state.emspLocations);
  const [selectedLocation, setSelectedLocation] = useState<OcpiLocation | null>(null);
  const [pulling, setPulling] = useState(false);
  const [pullSuccessMessage, setPullSuccessMessage] = useState<string | null>(null);

  // ── Data fetching ──────────────────────────────────────────────────────────

  /**
   * Fetches eMSP locations for this role with the given pagination/sort parameters.
   * @param {any} data - The pagination/sort parameters.
   */
  const fetchData = (data: ApiRequest) => {
    if (!loading) {
      dispatch(
        fetchEmspLocations({
          countryCode,
          partyId,
          counterPartyId: filterPartyId,
          counterCountryCode: filterCountryCode,
          ...data,
        }),
      );
    }
  };

  useEffect(() => {
    dispatch(
      fetchEmspLocations({
        countryCode,
        partyId,
        counterPartyId: filterPartyId,
        counterCountryCode: filterCountryCode,
      }),
    );
  }, [countryCode, partyId, filterPartyId, filterCountryCode, dispatch, refreshTrigger]);

  const columns = [
    {
      label: t('ocpi:created'),
      accessorKey: '_createdAt' as keyof LocationRow,
      sortAllow: false,
      /**
       * Rendering created date
       * @param {LocationRow} currentRow The current location row.
       * @returns {any} The rendered created date element.
       */
      customData: (currentRow: LocationRow) => (
        <span>{ConvertUTCtoDateTime(currentRow._createdAt || currentRow.createdAt)}</span>
      ),
    },
    {
      label: t('ocpi:locationId'),
      accessorKey: 'id' as keyof LocationRow,
      sortAllow: false,
      columnWidth: 260,
      /**
       * Renders the location ID in a code badge.
       * @param {any} row The location row.
       */
      customData: (row: LocationRow) => <code className="cpo-location-id-badge">{row.id}</code>,
    },
    {
      label: t('ocpi:name'),
      accessorKey: 'name' as keyof LocationRow,
      sortAllow: false,
    },
    {
      label: t('ocpi:sourceCpo'),
      accessorKey: 'cpo' as keyof LocationRow,
      sortAllow: false,
      /**
       * Renders the source CPO badge.
       * @param {any} row The location row.
       */
      customData: (row: LocationRow) => <span className="cpo-source-cpo-badge">{row.cpo}</span>,
    },
    {
      label: t('ocpi:address'),
      accessorKey: 'address' as keyof LocationRow,
      sortAllow: false,
    },
    {
      label: t('ocpi:evses').toUpperCase(),
      accessorKey: 'evsesCount' as keyof LocationRow,
      sortAllow: false,
      /**
       * Renders the EVSEs count badge.
       * @param {any} row The location row.
       */
      customData: (row: LocationRow) => (
        <span className="cpo-evses-count-badge">
          {row.evsesCount} EVSE{row.evsesCount !== 1 ? 's' : ''}
        </span>
      ),
    },
    {
      label: t('ocpi:created'),
      accessorKey: 'createdAt' as keyof LocationRow,
      sortAllow: false,
    },
    {
      label: t('ocpi:lastUpdated').toUpperCase(),
      accessorKey: 'lastUpdated' as keyof LocationRow,
      sortAllow: false,
    },
  ];

  const tableData: LocationRow[] = locations.map((loc: OcpiLocation) => {
    const locId = loc.id || loc._objectId || '';
    const dateString = loc.last_updated || loc._lastUpdated;
    const lastUpdated = dateString ? new Date(dateString).toLocaleDateString() : '–';
    const addressString = [loc.address, loc.city, loc.country].filter(Boolean).join(', ') || '–';
    const sourceCpo =
      loc.cpoCountryCode && loc.cpoPartyId ? `${loc.cpoCountryCode} / ${loc.cpoPartyId}` : '–';
    const createdAt =
      loc._createdAt || loc.createdAt || (loc as any).created_at || (loc as any).created || '';

    return {
      id: locId,
      name: loc.name || '–',
      cpo: sourceCpo,
      address: addressString,
      evsesCount: loc.evses?.length ?? 0,
      lastUpdated,
      _createdAt: createdAt,
      createdAt,
      raw: loc,
    };
  });

  /**
   * Table pagination function
   * @param {any} value - The value
   */
  const pageAndLimitChange = (value: ApiRequest) => {
    fetchData({ ...value, sortField, sortBy });
  };

  /**
   * Handle the table's sort change.
   * @param {any} sortData - The sortData
   */
  const handleSortFunction = (sortData: { sortField: string; sortBy: 'asc' | 'desc' }) => {
    dispatch(setSortData(sortData));
    fetchData({
      sortBy: sortData.sortBy,
      sortField: sortData.sortField,
      page: DefaultApiLimit.page,
      limit,
    });
  };

  /**
   * Function to refresh data
   */
  const handleRefresh = () => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({ ...queryParameters, page, limit, sortField, sortBy });
  };

  /**
   * Manually pulls locations from the connected CPO for this eMSP role. Shows a success
   * modal and refreshes the table on success; errors (offline, no response, or a
   * partner-side failure) are surfaced via the shared notification toast.
   */
  const handlePull = async () => {
    setPulling(true);
    try {
      const result = await dispatch(
        pullEmspLocations({
          countryCode,
          partyId,
          counterPartyId: filterPartyId,
          counterCountryCode: filterCountryCode,
        }),
      ).unwrap();
      setPullSuccessMessage(result.message);
      handleRefresh();
    } catch {
      // Error notification already dispatched by the pullEmspLocations thunk.
    } finally {
      setPulling(false);
    }
  };

  return (
    <div className="cpo-locations-panel">
      {/* ── Top bar ── */}
      <div className="cpo-locations-topbar">
        <div>
          <h3 className="cpo-panel-title">{t('ocpi:receivedCpoLocations')}</h3>
          <p className="cpo-panel-subtitle">
            {locations.length > 0
              ? `${locations.length} location${
                  locations.length !== 1 ? 's' : ''
                } received from connected CPOs`
              : t('ocpi:noLocationsReceived')}
          </p>
        </div>
      </div>

      {/* ── Locations Table ── */}
      <CustomTable
        version="v2"
        header={columns}
        data={tableData}
        limit={limit}
        page={page}
        totalPages={totalPages}
        totalResults={totalResults}
        loading={loading}
        serialNo
        sortData={{ sortBy, sortField }}
        handleSortChange={handleSortFunction}
        pageLimitChange={pageAndLimitChange}
        filters={filters}
        filtersToDispatch={updateEMSPLocationsFilters}
        filterListLoading={filterListLoading}
        filterListToDispatch={fetchEmspLocationFilters}
        sizeChangeButtonRequired
        actionDropdown
        rowClickEvent={(_id, row) => setSelectedLocation(row.raw)}
        rowClickParamsKey="id"
        refershCurrentPages={handleRefresh}
        requiredRefreshToggle
        refreshFunction={handleRefresh}
        refreshRequired
        handleRefresh={handleRefresh}
        hoverLabel={t('ocpi:refresh')}
        rightTopContent={
          <MyButton
            label={pulling ? t('ocpi:pullingLocations') : t('ocpi:pullLocations')}
            buttonType="primary"
            svgIcon={<Download height={14} width={14} />}
            onClickFunc={handlePull}
            disableButton={pulling}
          />
        }
      />

      {/* ── Drill-down modal ── */}
      {selectedLocation && (
        <LocationDetailsModal
          location={selectedLocation}
          onClose={() => setSelectedLocation(null)}
        />
      )}

      {/* ── Pull success modal ── */}
      <ModalBox
        open={Boolean(pullSuccessMessage)}
        header={false}
        notification
        title={null}
        closeModal={() => setPullSuccessMessage(null)}
        content={
          <SuccessNotification
            title={t('ocpi:locationsPulledSuccessfully')}
            description={pullSuccessMessage || ''}
            onNotificationClose={() => setPullSuccessMessage(null)}
          />
        }
      />
    </div>
  );
}

EmspLocationsPanel.defaultProps = {
  filterCountryCode: undefined,
  filterPartyId: undefined,
  refreshTrigger: undefined,
};
